/**
 * Componente para os botões de ação do input de chave NFe
 */

"use client";
import {
  Button,
  Tooltiper,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Command,
  CommandList,
  CommandGroup,
  CommandItem,
  Loader,
} from "visuais";
import { AlertCircle } from "lucide-react";
import {
  IconClockHour8Filled,
  IconFileDownloadFilled,
  IconTrashFilled,
} from "@tabler/icons-react";

interface ChaveInputActionsProps {
  inputValue: string;
  isLoading: boolean;
  error: string | null;
  xmlHistory: string[];
  isPopoverOpen: boolean;
  isDownloadingDanfe: boolean;
  onDownloadDanfe: () => void;
  onHistorySelect: (chave: string) => void;
  onClearHistory: () => void;
  onPopoverOpenChange: (open: boolean) => void;
}

export function ChaveInputActions({
  inputValue,
  isLoading,
  error,
  xmlHistory,
  isPopoverOpen,
  isDownloadingDanfe,
  onDownloadDanfe,
  onHistorySelect,
  onClearHistory,
  onPopoverOpenChange,
}: ChaveInputActionsProps) {
  return (
    <div className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground flex gap-1 rounded-r-md px-1 items-center justify-end h-full">
      <Tooltiper content="Baixar DANFE" variant="default">
        <Button
          size="sm"
          variant="secondary"
          className="h-6 px-2 text-muted-foreground hover:text-destructive hover:h-7 transition-all duration-300 group bg-cyan-500/40 hover:bg-cyan-500/60"
          aria-label="Baixar DANFE"
          onClick={onDownloadDanfe}
          disabled={!inputValue?.trim() || isDownloadingDanfe}
        >
          {isDownloadingDanfe ? (
            <Loader className="size-6" />
          ) : (
            <span className="text-foreground text-xs group-hover:text-sm flex items-center gap-1  transition-all duration-300">
              <IconFileDownloadFilled className="size-4 group-hover:size-5" />
              DANFE
            </span>
          )}
        </Button>
      </Tooltiper>

      {error ? (
        <Tooltiper content={<p>{error}</p>} variant="error">
          <Button
            size="icon"
            variant="destructive"
            className="size-6 p-0 text-muted-foreground hover:text-destructive hover:size-7 transition-all duration-300 group"
            aria-label={`Erro na busca: ${error}`}
          >
            <AlertCircle className="size-5 text-foreground group-hover:size-6 transition-all duration-300" />
          </Button>
        </Tooltiper>
      ) : null}
      {xmlHistory.length > 0 ? (
        <Popover open={isPopoverOpen} onOpenChange={onPopoverOpenChange}>
          <PopoverTrigger asChild>
            <Button
              size="sm"
              variant="secondary"
              className="h-6 px-2 text-muted-foreground hover:text-destructive hover:h-7 transition-all duration-300 group bg-cyan-500/40 hover:bg-cyan-500/60"
              aria-label="Abrir histórico de chaves utilizadas"
            >
              {isLoading ? (
                <Loader className="size-6" />
              ) : (
                <span className="text-foreground text-xs group-hover:text-sm flex items-center gap-1  transition-all duration-300">
                  <IconClockHour8Filled className="size-4 group-hover:size-5" />
                  Histórico
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="p-0 w-[450px]">
            <Command>
              <CommandList>
                <CommandGroup
                  heading={
                    <div className="flex items-center justify-between w-full">
                      <span>Histórico Recente</span>
                      <Button
                        size="icon"
                        variant="destructive"
                        className="size-6 p-0 text-muted-foreground hover:text-destructive hover:size-7 transition-all duration-300 group"
                        onClick={(e) => {
                          e.stopPropagation();
                          onClearHistory();
                        }}
                        aria-label="Limpar histórico de chaves"
                      >
                        <IconTrashFilled className="size-4 text-foreground group-hover:size-5 transition-all duration-300" />
                      </Button>
                    </div>
                  }
                >
                  {xmlHistory.map((chave) => (
                    <CommandItem
                      key={chave}
                      onSelect={() => onHistorySelect(chave)}
                      className="cursor-pointer text-xs font-mono"
                      value={chave}
                    >
                      {chave}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
}
